/**
 * `<MapArchivePanel/>` — restore-from-archive (issue #1043 round-4 ⑤.b).
 *
 * QA round-4 finding A2: a lead who archived a parent + its subtree had no
 * way to bring any of it back. This panel is the restore surface — opened
 * from the Settings "Archive" row (lead-only at the entry point), it lists
 * every archived task in the team newest-first and offers a single calm
 * "Restore" action per row.
 *
 * Data shape:
 *   - Fetches `GET /api/teams/{teamId}/tasks/nodes?include_archived=true`
 *     (the existing route already supports the flag, see
 *     `backend/src/api/routes/taskmap/__init__.py`). The response includes
 *     BOTH live and archived nodes; the panel filters client-side to the
 *     archived ones (`archived_at !== null`). The full list is also used to
 *     walk a node's parent chain so the row can show its closest live
 *     ancestor as a calm context label ("Outcome: …"); a node whose every
 *     ancestor is archived shows "Outcome: —" (factual, no judgement).
 *   - Restore fires `useRestoreTaskNode`; on success the row is hidden via a
 *     local `dismissed` set so the panel re-renders calmly without waiting
 *     for the refetch (which `onSettled` triggers anyway). On error the row
 *     stays put with a single inline error message until the next click.
 *
 * Per-node only — there is NO cascade-restore (matches the backend). If a
 * lead cascade-archived a branch, the panel surfaces every node one at a
 * time; restoring the root un-archives only the root and the children
 * remain in the panel for individual restore.
 *
 * Brand firewall (§A9): no `burnout` / `overloaded` / `scorecard` / ranking
 * language — this is a panel about WORK, never about people. Copy is
 * factual and calm ("Restored ‘<title>’" — no celebration, no alarm).
 *
 * Editorial-aware: reads `useMapTheme().palette` and renders calm in both
 * legacy and editorial; the panel is presentational and never touches the
 * canvas/rail directly.
 *
 * Isolation: only generic UI primitives + the taskmap data layer; no
 * scheduler imports.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { apiFetch } from "../../api/client";
import { Button, ErrorState, Skeleton } from "../../components/ui";
import { type TaskNodeWithAssignments, useRestoreTaskNode } from "../api/taskNodes";
import { formatRelativeShort } from "../history/formatTime";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";

/** Cache key for the include_archived listing — separate from the live tree key. */
const ARCHIVED_NODES_KEY = (teamId: string) => ["task-nodes", teamId, "include-archived"] as const;

/**
 * Fetch the team's full node listing (live + archived). The result is the
 * same `TaskNodeWithAssignments[]` shape the live-tree query returns; the
 * panel filters client-side. Kept as a small hook here (not in
 * `taskNodes.ts`) so the archive surface owns its own listing without
 * polluting the live-tree data layer.
 */
function useTeamNodesIncludingArchived(teamId: string | undefined) {
  return useQuery({
    queryKey: ARCHIVED_NODES_KEY(teamId ?? ""),
    enabled: Boolean(teamId),
    retry: false,
    queryFn: () =>
      apiFetch<TaskNodeWithAssignments[]>(`/api/teams/${teamId}/tasks/nodes?include_archived=true`),
  });
}

/**
 * Walk a node's parent chain (over the full list, archived included) until
 * a LIVE ancestor is found. Returns its title, or `null` if every ancestor
 * is archived / the node has no live ancestor (it was a root). A pure
 * helper — easy to test in isolation.
 */
function findLiveAncestorTitle(list: TaskNodeWithAssignments[], nodeId: string): string | null {
  const byId = new Map(list.map((n) => [n.id, n] as const));
  let current = byId.get(nodeId);
  const seen = new Set<string>();
  while (current?.parent_id) {
    if (seen.has(current.id)) return null; // corrupt cycle guard
    seen.add(current.id);
    const parent = byId.get(current.parent_id);
    if (!parent) return null;
    if (parent.archived_at === null) return parent.title;
    current = parent;
  }
  return null;
}

export interface MapArchivePanelProps {
  teamId: string | undefined;
}

export function MapArchivePanel({ teamId }: MapArchivePanelProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const nodesQuery = useTeamNodesIncludingArchived(teamId);
  const qc = useQueryClient();
  // Dismissed (restored or hidden) ids — local to the panel so the row
  // disappears optimistically; the refetch eventually agrees.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Per-row error message — only the most recent restore failure shows.
  const [errorByNodeId, setErrorByNodeId] = useState<Record<string, string>>({});
  // Most-recent restored title (factual "Restored ‘<title>’" flash).
  const [flash, setFlash] = useState<string | null>(null);

  const restore = useRestoreTaskNode(teamId, {
    onSuccess: () => {
      // Invalidate the archive listing too; the live-tree key is invalidated
      // by the shared hook itself.
      if (teamId) {
        void qc.invalidateQueries({ queryKey: ARCHIVED_NODES_KEY(teamId) });
      }
    },
  });

  // The archived sub-list, newest-first by archived_at.
  const archived = useMemo(() => {
    const all = nodesQuery.data ?? [];
    return all
      .filter((n) => n.archived_at !== null)
      .filter((n) => !dismissed.has(n.id))
      .slice()
      .sort((a, b) => {
        // archived_at is non-null here (filtered above), but TS doesn't know.
        const ax = a.archived_at ?? "";
        const bx = b.archived_at ?? "";
        return bx.localeCompare(ax);
      });
  }, [nodesQuery.data, dismissed]);

  const handleRestore = (node: TaskNodeWithAssignments) => {
    setErrorByNodeId((prev) => {
      const next = { ...prev };
      delete next[node.id];
      return next;
    });
    restore.mutate(
      { nodeId: node.id },
      {
        onSuccess: () => {
          setDismissed((prev) => {
            const next = new Set(prev);
            next.add(node.id);
            return next;
          });
          setFlash(`Restored ‘${node.title}’`);
          // Auto-dismiss the flash after a calm pause.
          window.setTimeout(() => {
            setFlash((current) => (current === `Restored ‘${node.title}’` ? null : current));
          }, 3500);
        },
        onError: () => {
          setErrorByNodeId((prev) => ({
            ...prev,
            [node.id]: "Couldn’t restore that one. Try again.",
          }));
        },
      },
    );
  };

  return (
    <div
      data-testid="map-archive-panel"
      className="rounded-2xl p-6"
      style={{
        backgroundColor: editorial ? c.canvas : "#FFFFFF",
        border: `1px solid ${editorial ? c.rule : "#E2E8E5"}`,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      <ArchiveHeader theme={theme} />
      <ArchiveBody
        theme={theme}
        nodesQuery={nodesQuery}
        archived={archived}
        errorByNodeId={errorByNodeId}
        restorePendingNodeId={restore.isPending ? restore.variables?.nodeId : undefined}
        onRestore={handleRestore}
        allNodes={nodesQuery.data ?? []}
      />
      {flash ? (
        <p
          data-testid="archive-panel-flash"
          className="mt-4 text-[12px]"
          style={{ color: editorial ? c.slate : "#4B5563" }}
        >
          {flash}
        </p>
      ) : null}
    </div>
  );
}

function ArchiveHeader({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div className="mb-4">
      {editorial ? (
        <>
          <div
            className="text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
          >
            Archive
          </div>
          <h3
            data-testid="archive-panel-heading"
            className="mt-1 text-[22px] italic leading-tight"
            style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
          >
            Restore tasks you’ve removed
          </h3>
          <p className="mt-2 text-[12.5px]" style={{ color: c.muted }}>
            Restore tasks that were removed from the map. They keep their history.
          </p>
        </>
      ) : (
        <>
          <h3
            data-testid="archive-panel-heading"
            className="text-sm font-bold"
            style={{ color: "#0C2A2A" }}
          >
            Archive
          </h3>
          <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
            Restore tasks that were removed from the map. They keep their history.
          </p>
        </>
      )}
    </div>
  );
}

interface ArchiveBodyProps {
  theme: MapTheme;
  nodesQuery: ReturnType<typeof useTeamNodesIncludingArchived>;
  archived: TaskNodeWithAssignments[];
  errorByNodeId: Record<string, string>;
  restorePendingNodeId: string | undefined;
  onRestore: (node: TaskNodeWithAssignments) => void;
  allNodes: TaskNodeWithAssignments[];
}

function ArchiveBody({
  theme,
  nodesQuery,
  archived,
  errorByNodeId,
  restorePendingNodeId,
  onRestore,
  allNodes,
}: ArchiveBodyProps) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  if (nodesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2" data-testid="archive-panel-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (nodesQuery.isError) {
    return (
      <ErrorState
        title="Couldn’t load the archive"
        message="Refresh the page; if it keeps happening, check your connection and try again."
      />
    );
  }

  if (archived.length === 0) {
    return (
      <p
        data-testid="archive-panel-empty"
        className="py-6 text-center text-[12.5px] italic"
        style={{ color: editorial ? c.muted : "#9CA3AF" }}
      >
        No archived tasks. Anything you remove from the map will land here, recoverable.
      </p>
    );
  }

  return (
    <ul
      data-testid="archive-panel-list"
      className="overflow-hidden"
      style={{
        border: `1px solid ${editorial ? c.rule : "#F3F4F6"}`,
        borderRadius: 10,
      }}
    >
      {archived.map((node, idx) => {
        const ancestorTitle = findLiveAncestorTitle(allNodes, node.id);
        const stamp = node.archived_at ? formatRelativeShort(node.archived_at) : "";
        const error = errorByNodeId[node.id];
        const isPending = restorePendingNodeId === node.id;
        return (
          <li
            key={node.id}
            data-testid={`archive-panel-row-${node.id}`}
            className="flex items-center gap-3 px-4 py-3"
            style={{
              borderTop: idx === 0 ? undefined : `1px solid ${editorial ? c.rule : "#F3F4F6"}`,
              backgroundColor: editorial ? c.cardBg : "#FBFCFB",
            }}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span
                  className="text-[10px] uppercase"
                  style={{
                    fontFamily: theme.fonts.mono,
                    color: editorial ? c.muted : "#9CA3AF",
                    letterSpacing: "0.08em",
                  }}
                >
                  (archived)
                </span>
                <span
                  className="truncate text-[13.5px] font-medium"
                  style={{ color: editorial ? c.ink : "#0C2A2A" }}
                >
                  {node.title}
                </span>
              </div>
              <div
                className="mt-1 flex items-center gap-2 text-[11.5px]"
                style={{ color: editorial ? c.muted : "#6B7280" }}
              >
                <span data-testid={`archive-panel-context-${node.id}`}>
                  Outcome: {ancestorTitle ?? "—"}
                </span>
                <span aria-hidden>·</span>
                <span
                  data-testid={`archive-panel-when-${node.id}`}
                  title={node.archived_at ?? undefined}
                >
                  archived {stamp || "earlier"}
                </span>
              </div>
              {error ? (
                <p
                  data-testid={`archive-panel-error-${node.id}`}
                  className="mt-1 text-[11.5px]"
                  style={{ color: "#B45309" }}
                >
                  {error}
                </p>
              ) : null}
            </div>
            <Button
              data-testid={`archive-panel-restore-${node.id}`}
              variant="secondary"
              onClick={() => onRestore(node)}
              disabled={isPending}
              aria-label={`Restore ${node.title}`}
            >
              {isPending ? "Restoring…" : "Restore"}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

// Re-exported for tests that want to assert helper behaviour in isolation.
export { findLiveAncestorTitle };
